// T2T-05-C6 — Admin-only collapsible section that lists vendor chat tokens
// for this request and provides an "Invite vendor" inline form. Renders inside
// EmbeddedChat above the message list. Hidden for non-admins.

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  listVendorChatTokens,
  issueVendorChatToken,
  revokeVendorChatToken,
} from "../../../../../api/t2tMessagesApi";

function _formatExpiry(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
}

export default function VendorInvitePanel({ requestId }) {
  const [open,    setOpen]    = useState(false);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const [inviting, setInviting]   = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName,  setInviteName]  = useState("");
  const [lastIssuedUrl, setLastIssuedUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const r = await listVendorChatTokens(requestId);
    if (r.error) setError(r.error.message || "Could not load vendor invites.");
    else setRows(r.data?.data || []);
    setLoading(false);
  }, [requestId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setError(null);
    setLastIssuedUrl(null);
    const r = await issueVendorChatToken(requestId, {
      vendor_email: email,
      vendor_name:  inviteName.trim() || null,
      send_email:   true,
    });
    setInviting(false);
    if (r.error) {
      setError(r.error.message || "Could not issue token.");
      return;
    }
    const data = r.data?.data;
    if (data?.portal_url) setLastIssuedUrl(data.portal_url);
    setInviteEmail("");
    setInviteName("");
    load();
  };

  const handleRevoke = async (tokenId) => {
    if (!window.confirm("Revoke this vendor link? They will lose access immediately.")) return;
    const r = await revokeVendorChatToken(requestId, tokenId);
    if (r.error) {
      setError(r.error.message || "Could not revoke.");
      return;
    }
    load();
  };

  return (
    <div style={{
      border: "1px solid var(--t2t-border)",
      borderRadius: "var(--t2t-radius)",
      marginBottom: 12,
      overflow: "hidden",
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px",
          background: "var(--t2t-bg)",
          border: "none",
          fontSize: 12, fontWeight: 600,
          color: "var(--t2t-text-secondary)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} />
        <i className="bi bi-people" />
        <span>External vendor access</span>
        <span style={{ flex: 1 }} />
        {rows.length > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 700,
            background: "var(--t2t-navy)", color: "#fff",
            padding: "2px 6px", borderRadius: 4,
          }}>
            {rows.filter(r => r.is_active).length} active
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: "10px 12px", background: "var(--t2t-surface)" }}>
          {error && (
            <div style={{
              fontSize: 11, padding: "6px 8px", marginBottom: 8,
              background: "#FEE2E2", color: "#DC2626",
              border: "1px solid #FECACA", borderRadius: 6,
            }}>{error}</div>
          )}

          {/* Invite form */}
          <form onSubmit={handleInvite} style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input
              type="email"
              required
              placeholder="vendor@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              style={{
                flex: 2, fontSize: 12, padding: "6px 8px",
                border: "1px solid var(--t2t-border)", borderRadius: 6,
                outline: "none",
              }}
            />
            <input
              type="text"
              placeholder="Name (optional)"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              style={{
                flex: 1, fontSize: 12, padding: "6px 8px",
                border: "1px solid var(--t2t-border)", borderRadius: 6,
                outline: "none",
              }}
            />
            <button
              type="submit"
              className="t2t-btn t2t-btn-primary t2t-btn-sm"
              disabled={inviting || !inviteEmail.trim()}
            >
              {inviting ? "Inviting…" : "Invite"}
            </button>
          </form>

          {lastIssuedUrl && (
            <div style={{
              fontSize: 10, padding: "6px 8px", marginBottom: 10,
              background: "#DCFCE7", color: "#166534",
              border: "1px solid #86EFAC", borderRadius: 6,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="bi bi-check-circle" />
              <span style={{ flex: 1 }}>Invite sent. Magic link copied below — share if email fails.</span>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(lastIssuedUrl); }}
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                style={{ padding: "0 4px" }}
                title="Copy link"
              >
                <i className="bi bi-clipboard" />
              </button>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div style={{ fontSize: 11, padding: 8, textAlign: "center", color: "var(--t2t-text-muted)" }}>
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize: 11, padding: 8, textAlign: "center", color: "var(--t2t-text-muted)" }}>
              No vendor invitations yet.
            </div>
          ) : (
            <div style={{ borderTop: "1px solid var(--t2t-border)" }}>
              {rows.map(t => (
                <div key={t.token_id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 0", borderBottom: "1px solid var(--t2t-border)",
                  fontSize: 11,
                }}>
                  <i className="bi bi-person-badge" style={{ color: t.is_active ? "var(--t2t-navy)" : "var(--t2t-text-muted)" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "var(--t2t-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.vendor_name || t.vendor_email}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>
                      {t.vendor_email}
                      {" · "}
                      {t.is_active ? `expires ${_formatExpiry(t.expires_at)}` : "revoked"}
                      {t.last_used_at && t.is_active ? ` · last seen ${_formatExpiry(t.last_used_at)}` : ""}
                    </div>
                  </div>
                  {t.is_active && (
                    <button
                      type="button"
                      className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                      onClick={() => handleRevoke(t.token_id)}
                      title="Revoke link"
                      style={{ color: "var(--t2t-danger)", padding: "2px 6px", fontSize: 10 }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

VendorInvitePanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
